/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import * as Prismic from '@prismicio/client'
import { type PrismicDocument, type Query } from '@prismicio/client'

/**
RATIONALE FOR ANY AND TYPECASTING TO T:
YOU CAN NOT TYPE THIS IN A GENERIC WAY, IT DEPENDS WHICH DOCUMENT

TODO: ditch this prismic setup, or solve it

See for example this error:
Type 'T & { uid: string | null; }' does not satisfy the constraint 'AllDocumentTypes'.
Type 'T & { uid: string | null; }' is not assignable to type 'Table2ColumnsDocument<string>'.
Types of property 'uid' are incompatible. Type 'string | null' is not assignable to type 'null'.
Type 'string' is not assignable to type 'null'.
*/

/**
 * @prismicio/client uses either a Prismic repository name or a repository-specific Document API endpoint to query content from Prismic.
 * When proxying a Prismic API v2 repository endpoint, the documentAPIEndpoint option can be used to specify that endpoint.
 * https://github.com/prismicio/prismic-client/blob/v7.13.1/messages/prefer-repository-name.md
 */
const getApiClient = (endpoint: string, accessToken?: string) => {
  if (endpoint.includes('go-prismic')) {
    const repositoryName = endpoint.replace(/.*go-prismic\/([^/]+).*/, '$1')
    return Prismic.createClient(repositoryName, {
      documentAPIEndpoint: endpoint,
      ...(accessToken && {
        fetchOptions: {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      }),
    })
  }
  return Prismic.createClient(endpoint, {})
}

type BasePrismicQueryArgs = {
  endpoint: string
  options?: Prismic.QueryParams
  accessToken?: string
}

type PrismicError = Prismic.PrismicError<Response>

type PrismicDataType = Record<string, any>

// eslint-disable-next-line @typescript-eslint/naming-convention
async function getByUID<T extends PrismicDataType>(
  endpoint: string,
  type: string,
  uid: string,
  options: Prismic.QueryParams,
  accessToken?: string
): Promise<PrismicDocument<T>> {
  const api = getApiClient(endpoint, accessToken)
  return (await api.getByUID<any>(type, uid, options)) as PrismicDocument<T>
}

// eslint-disable-next-line @typescript-eslint/naming-convention
async function getByID<T extends PrismicDataType>(
  endpoint: string,
  id: string,
  options: Prismic.QueryParams,
  accessToken?: string
): Promise<PrismicDocument<T>> {
  const api = getApiClient(endpoint, accessToken)
  return (await api.getByID<any>(id, options)) as PrismicDocument<T>
}

// eslint-disable-next-line @typescript-eslint/naming-convention
async function getByIDs<T extends PrismicDataType>(
  endpoint: string,
  ids: string[],
  options: Prismic.QueryParams,
  accessToken?: string
): Promise<Query<PrismicDocument<T>>> {
  const api = getApiClient(endpoint, accessToken)
  return (await api.getByIDs<any>(ids, options)) as unknown as Query<PrismicDocument<T>> // | null
}

async function getByQuery<T extends PrismicDataType>(
  endpoint: string,
  query: string | string[],
  options: Prismic.QueryParams,
  accessToken?: string
): Promise<Query<PrismicDocument<T>>> {
  const api = getApiClient(endpoint, accessToken)
  return (await api.get<any>({
    predicates: query,
    ...options,
  })) as unknown as Query<PrismicDocument<T>> // | null
}

const defaultOptions = {}

type UsePrismicUIDArgs = BasePrismicQueryArgs & {
  documentType: string
  uid: string
}

export interface ExtendedPrismicQueryParams extends Prismic.QueryParams {
  enabled?: boolean
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const usePrismicUID = <TQueryFnData extends PrismicDataType, TData = PrismicDocument<TQueryFnData>>(
  { endpoint, documentType, options: prismicOptions = defaultOptions, uid, accessToken }: UsePrismicUIDArgs,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getByUID<TQueryFnData>>>, PrismicError, TData>>
  }
): UseQueryResult<TData, PrismicError> =>
  useQuery({
    queryKey: ['prismic/getByUID', endpoint, documentType, uid, prismicOptions, Boolean(accessToken)],
    queryFn: () => getByUID<TQueryFnData>(endpoint, documentType, uid, prismicOptions, accessToken),
    ...options?.query,
  })

type UsePrismicIdArgs = BasePrismicQueryArgs & {
  id: string
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const usePrismicID = <TQueryFnData extends PrismicDataType, TData = PrismicDocument<TQueryFnData>>(
  { endpoint, id, options: prismicOptions = defaultOptions, accessToken }: UsePrismicIdArgs,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getByID<TQueryFnData>>>, PrismicError, TData>>
  }
): UseQueryResult<TData, PrismicError> =>
  useQuery({
    queryKey: ['prismic/getByID', endpoint, id, prismicOptions, Boolean(accessToken)],
    queryFn: () => getByID<TQueryFnData>(endpoint, id, prismicOptions, accessToken),
    ...options?.query,
  })

type UsePrismicIdsArgs = BasePrismicQueryArgs & {
  ids: string[]
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const usePrismicIDs = <TQueryFnData extends PrismicDataType, TData = Query<PrismicDocument<TQueryFnData>>>(
  { endpoint, ids, options: prismicOptions = defaultOptions, accessToken }: UsePrismicIdsArgs,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getByIDs<TQueryFnData>>>, PrismicError, TData>>
  }
): UseQueryResult<TData, PrismicError> =>
  useQuery({
    queryKey: ['prismic/getByIDs', endpoint, ids, prismicOptions, Boolean(accessToken)],
    queryFn: () => getByIDs<TQueryFnData>(endpoint, ids, prismicOptions, accessToken),
    ...options?.query,
  })

type UsePrismicQueryArgs = BasePrismicQueryArgs & {
  query: string | string[]
}

export const usePrismicQuery = <TQueryFnData extends PrismicDataType, TData = Query<PrismicDocument<TQueryFnData>>>(
  { endpoint, query, options: prismicOptions = defaultOptions, accessToken }: UsePrismicQueryArgs,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getByQuery<TQueryFnData>>>, PrismicError, TData>>
  }
): UseQueryResult<TData, PrismicError> =>
  useQuery({
    queryKey: ['prismic/getByQuery', endpoint, query, prismicOptions, Boolean(accessToken)],
    queryFn: () => getByQuery<TQueryFnData>(endpoint, query, prismicOptions, accessToken),
    ...options?.query,
  })
